<template>
  <div class="p-4 max-w-screen-lg mx-auto text-sm">

    <div class="page-header">
      <h1>Stok Titipan — Clair Bakery</h1>
      <p class="subtitle">Kelola stok produk titipan yang disimpan di freezer Imah Kopi</p>
    </div>

    <!-- ================= SELECT PRODUK + STOK SAAT INI ================= -->
    <div class="stock-summary-card">
      <div class="form-group product-select">
        <label class="form-label">Produk</label>
        <select v-model="selectedProductId" @change="onProductChange">
          <option
            v-for="p in products"
            :key="p.consignment_product_id"
            :value="p.consignment_product_id"
          >
            {{ p.product_name }}
          </option>
        </select>
      </div>

      <div class="current-stock-box">
        <span class="label">Sisa Stok Saat Ini</span>
        <span class="value">{{ currentStock }} pcs</span>
      </div>
    </div>

    <!-- ================= TOP REPEAT CUSTOMERS ================= -->
    <div v-if="topCustomers.length" class="top-customers-card">
      <div class="section-title">Top Repeat Customer</div>
      <table class="top-customers-table">
        <thead>
          <tr>
            <th>Nama Pembeli</th>
            <th class="text-right">Jumlah Order</th>
            <th class="text-right">Total Qty</th>
            <th>Order Terakhir</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(c, i) in topCustomers" :key="i">
            <td>{{ c.billing_name }}</td>
            <td class="text-right order-count">{{ c.order_count }}x</td>
            <td class="text-right">{{ c.total_qty }} pcs</td>
            <td>{{ formatDate(c.last_order_at) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ================= INPUT STOK MASUK ================= -->
    <div class="input-stock-card">
      <div class="form-label section-title">Tambah Stok (Kirim Barang)</div>

      <div class="form-row">
        <div class="form-group">
          <label class="form-label">Qty Masuk</label>
          <input type="number" v-model.number="inputQty" min="1" placeholder="Contoh: 20" />
        </div>

        <div class="form-group note-group">
          <label class="form-label">Catatan (opsional)</label>
          <input type="text" v-model="inputNote" placeholder="Contoh: Kirim rutin mingguan" />
        </div>

        <div class="form-group btn-group">
          <label class="form-label">&nbsp;</label>
          <button class="btn-save" :disabled="saving" @click="saveStock">
            {{ saving ? "Menyimpan..." : "Simpan Stok" }}
          </button>
        </div>
      </div>
    </div>

    <!-- ================= HISTORY TABLE ================= -->
    <div class="history-card">
      <div class="history-header">
        <div class="section-title">Riwayat Stok</div>
        <div v-if="unpaidCount > 0" class="unpaid-alert">
          ⚠ Piutang belum dibayar: <strong>Rp{{ formatRupiah(unpaidTotalNominal) }}</strong>
          ({{ unpaidCount }} transaksi, {{ unpaidTotalQty }} pcs)
        </div>
      </div>

      <div v-if="loadingHistory" class="empty-state">Memuat riwayat...</div>

      <div v-else-if="!history.length" class="empty-state">
        Belum ada riwayat untuk produk ini.
      </div>

      <table v-else class="history-table">
        <thead>
          <tr>
            <th>Tanggal</th>
            <th>No. Invoice</th>
            <th>Nama Pembeli</th>
            <th class="text-right">Masuk</th>
            <th class="text-right">Keluar</th>
            <th class="text-right">Saldo</th>
            <th class="text-right">Nominal</th>
            <th class="text-center">Status Bayar</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in history" :key="i">
            <td>{{ formatDate(row.tanggal) }}</td>
            <td>
              <span v-if="row.invoice_number">{{ row.invoice_number }}</span>
              <span v-else class="muted">{{ row.note || "Kirim Barang" }}</span>
            </td>
            <td>{{ row.billing_name || "-" }}</td>
            <td class="text-right in-value">
              <span v-if="row.masuk">+{{ row.masuk }}</span>
            </td>
            <td class="text-right out-value">
              <span v-if="row.keluar">-{{ row.keluar }}</span>
            </td>
            <td class="text-right saldo-value">{{ row.saldo }}</td>
            <td class="text-right nominal-value">
              <span v-if="row.source === 'invoice_deduction' && row.keluar">Rp{{ formatRupiah(row.keluar * HARGA_PER_PCS) }}</span>
              <span v-else class="muted">-</span>
            </td>
            <td class="text-center">
              <span v-if="row.source !== 'invoice_deduction'" class="muted">-</span>
              <button
                v-else
                class="pay-badge"
                :class="row.payment_status === 'lunas' ? 'is-paid' : 'is-unpaid'"
                @click="togglePaid(row)"
              >
                {{ row.payment_status === 'lunas' ? '✓ Lunas' : 'Belum Bayar' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

  </div>
</template>

<script>
import Swal from "sweetalert2";

export default {
  name: "ConsignmentStockPage",

  data() {
    return {
      HARGA_PER_PCS: 130000, // 🔥 harga fix per pcs, sesuaikan kalau produk beda harga nanti

      products: [],
      selectedProductId: null,
      currentStock: 0,

      inputQty: null,
      inputNote: "",
      saving: false,

      history: [],
      loadingHistory: false,

      topCustomers: [],
    };
  },

  async mounted() {
    await this.loadProducts();
  },

  computed: {
    unpaidRows() {
      return this.history.filter(
        (r) => r.source === "invoice_deduction" && r.payment_status !== "lunas"
      );
    },
    unpaidCount() {
      return this.unpaidRows.length;
    },
    unpaidTotalQty() {
      return this.unpaidRows.reduce((t, r) => t + (r.keluar || 0), 0);
    },
    unpaidTotalNominal() {
      return this.unpaidTotalQty * this.HARGA_PER_PCS;
    },
  },

  methods: {
    async loadProducts() {
      const res = await fetch("/api/get_consignment_products.php");
      const json = await res.json();

      if (json.status !== "success") {
        Swal.fire("Error", "Gagal load produk titipan", "error");
        return;
      }

      this.products = json.data || [];

      if (this.products.length) {
        this.selectedProductId = this.products[0].consignment_product_id;
        this.currentStock = this.products[0].current_stock;
        await this.loadHistory();
        await this.loadTopCustomers();
      }
    },

    onProductChange() {
      const p = this.products.find(
        (x) => x.consignment_product_id === this.selectedProductId
      );
      this.currentStock = p ? p.current_stock : 0;
      this.loadHistory();
      this.loadTopCustomers();
    },

    async loadTopCustomers() {
      if (!this.selectedProductId) return;

      try {
        const res = await fetch(
          `/api/get_consignment_top_customers.php?consignment_product_id=${this.selectedProductId}`
        );
        const json = await res.json();

        if (json.status === "success") {
          this.topCustomers = json.data || [];
        }
      } catch (err) {
        console.error(err);
      }
    },

    async loadHistory() {
      if (!this.selectedProductId) return;

      this.loadingHistory = true;
      try {
        const res = await fetch(
          `/api/get_consignment_history.php?consignment_product_id=${this.selectedProductId}`
        );
        const json = await res.json();

        if (json.status !== "success") {
          Swal.fire("Error", "Gagal load riwayat stok", "error");
          return;
        }

        this.history = json.data || [];
        this.currentStock = json.current_stock ?? this.currentStock;
      } finally {
        this.loadingHistory = false;
      }
    },

    async saveStock() {
      if (!this.selectedProductId) {
        Swal.fire("Pilih produk dulu");
        return;
      }

      if (!this.inputQty || this.inputQty <= 0) {
        Swal.fire("Qty harus lebih dari 0");
        return;
      }

      this.saving = true;

      try {
        const res = await fetch("/api/consignment_add_stock.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            consignment_product_id: this.selectedProductId,
            qty: this.inputQty,
            note: this.inputNote,
          }),
        });

        const json = await res.json();

        if (json.status !== "success") {
          Swal.fire("Gagal", json.message || "Terjadi kesalahan", "error");
          return;
        }

        Swal.fire({
          icon: "success",
          title: "Stok berhasil ditambahkan",
          timer: 1200,
          showConfirmButton: false,
        });

        this.inputQty = null;
        this.inputNote = "";

        // refresh stok produk di dropdown + history
        await this.loadProducts();

      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Server error", "error");
      } finally {
        this.saving = false;
      }
    },

    async togglePaid(row) {
      const newStatus = row.payment_status === "lunas" ? "belum_dibayar" : "lunas";

      const confirm = await Swal.fire({
        title: newStatus === "lunas" ? "Tandai sudah lunas?" : "Tandai belum dibayar?",
        text: `Invoice ${row.invoice_number || "-"} — ${row.keluar} pcs`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Ya, update",
        cancelButtonText: "Batal",
      });

      if (!confirm.isConfirmed) return;

      try {
        const res = await fetch("/api/consignment_mark_paid.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            log_id: row.log_id,
            payment_status: newStatus,
          }),
        });

        const json = await res.json();

        if (json.status !== "success") {
          Swal.fire("Gagal", json.message || "Terjadi kesalahan", "error");
          return;
        }

        row.payment_status = newStatus;

      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Server error", "error");
      }
    },

    formatRupiah(n) {
      return Number(n || 0).toLocaleString("id-ID");
    },

    formatDate(dateStr) {
      if (!dateStr) return "-";
      const d = new Date(dateStr.replace(" ", "T"));
      return d.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }) + " " + d.toLocaleTimeString("id-ID", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@200..900&display=swap');

* {
  font-family: "Outfit", sans-serif;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h1 {
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.subtitle {
  font-size: 13px;
  color: #6b7280;
  margin-top: 4px;
}

/* ============================= */
/* STOCK SUMMARY CARD */
/* ============================= */
.stock-summary-card {
  background: #f8f9fc;
  padding: 20px 24px;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0,0,0,.04);
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.product-select {
  flex: 1;
  min-width: 240px;
  margin-bottom: 0;
}

.current-stock-box {
  background: #111827;
  color: #fff;
  padding: 14px 22px;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 160px;
}

.current-stock-box .label {
  font-size: 12px;
  opacity: .7;
}

.current-stock-box .value {
  font-size: 24px;
  font-weight: 700;
  margin-top: 2px;
}

/* ============================= */
/* TOP CUSTOMERS CARD */
/* ============================= */
.top-customers-card {
  background: #fff;
  padding: 20px 24px;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0,0,0,.04);
  border: 1px solid #eef0f4;
  margin-bottom: 20px;
}

.top-customers-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.top-customers-table thead tr {
  border-bottom: 2px solid #111;
}

.top-customers-table th {
  text-align: left;
  padding: 8px;
  font-weight: 600;
  color: #374151;
}

.top-customers-table th.text-right,
.top-customers-table td.text-right {
  text-align: right;
}

.top-customers-table td {
  padding: 8px;
  border-bottom: 1px solid #f0f0f3;
}

.order-count {
  font-weight: 700;
  color: #3b82f6;
}

/* ============================= */
/* INPUT STOCK CARD */
/* ============================= */
.input-stock-card {
  background: #f8f9fc;
  padding: 20px 24px;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0,0,0,.04);
  margin-bottom: 24px;
}

.section-title {
  font-weight: 700;
  font-size: 14px;
  color: #111;
  margin-bottom: 14px;
}

.note-group {
  flex: 2;
}

.btn-group {
  flex: 0 0 auto;
}

.btn-save {
  background: #10b981;
  color: #fff;
  padding: 10px 22px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 14px;
  border: none;
  cursor: pointer;
  transition: background .2s ease, transform .15s ease;
  white-space: nowrap;
}

.btn-save:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
}

.btn-save:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

/* ============================= */
/* HISTORY CARD */
/* ============================= */
.history-card {
  background: #fff;
  padding: 20px 24px;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0,0,0,.04);
  border: 1px solid #eef0f4;
}

.history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}

.history-header .section-title {
  margin-bottom: 0;
}

.unpaid-alert {
  background: #fef3c7;
  color: #92400e;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 999px;
}

.text-center {
  text-align: center;
}

.pay-badge {
  border: none;
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity .2s ease, transform .15s ease;
  white-space: nowrap;
}

.pay-badge:hover {
  transform: translateY(-1px);
  opacity: .85;
}

.pay-badge.is-paid {
  background: #d1fae5;
  color: #059669;
}

.pay-badge.is-unpaid {
  background: #fee2e2;
  color: #dc2626;
}

.empty-state {
  text-align: center;
  color: #9ca3af;
  padding: 32px 0;
  font-size: 13px;
}

.history-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.history-table thead tr {
  border-bottom: 2px solid #111;
}

.history-table th {
  text-align: left;
  padding: 10px 8px;
  font-weight: 600;
  color: #374151;
}

.history-table th.text-right,
.history-table td.text-right {
  text-align: right;
}

.history-table td {
  padding: 10px 8px;
  border-bottom: 1px solid #f0f0f3;
  vertical-align: middle;
}

.history-table tbody tr:hover {
  background: #fafbfc;
}

.text-right {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.in-value span {
  color: #059669;
  font-weight: 600;
}

.out-value span {
  color: #dc2626;
  font-weight: 600;
}

.saldo-value {
  font-weight: 700;
  color: #111827;
}

.nominal-value {
  font-weight: 600;
  color: #374151;
}

.muted {
  color: #9ca3af;
  font-style: italic;
}

/* ============================= */
/* SHARED FORM STYLES */
/* ============================= */
.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 0;
}

.form-label {
  font-weight: 600;
  font-size: 13px;
  color: #111;
}

.form-row {
  display: flex;
  gap: 16px;
  align-items: flex-end;
  flex-wrap: wrap;
}

input[type="text"],
input[type="number"],
select {
  width: 100%;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 14px;
  color: #111;
  transition: border-color .2s ease, box-shadow .2s ease;
}

input:focus,
select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59,130,246,.15);
}

@media (max-width: 640px) {
  .stock-summary-card {
    flex-direction: column;
    align-items: stretch;
  }

  .current-stock-box {
    align-items: flex-start;
  }

  .form-row {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
